import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import {
    createContext,
    useContext,
    useEffect,
    useRef,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'
import { ScreenOverlay } from '@/React/Shared/Component/ScreenOverlay'
import { DropdownItem } from '@/React/Shared/Component/DropdownItem'

interface DropdownContextValue {
    show: boolean
    onClose?: () => void
    horizontalPosition: 'left' | 'right'
    verticalPosition: 'top' | 'bottom'
}

const DropdownContext = createContext<DropdownContextValue>({
    show: false,
    horizontalPosition: 'left',
    verticalPosition: 'bottom',
})

export interface DropdownContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'content'
> {
    show?: boolean
    horizontalPosition?: 'left' | 'right'
    verticalPosition?: 'top' | 'bottom'
    onClose?: () => void
    children?: ReactNode
}

export function DropdownContainer({
    show = false,
    horizontalPosition,
    verticalPosition = 'bottom',
    onClose,
    children,
    className = '',
    ...restProps
}: DropdownContainerProps) {
    const effectiveHorizontal = horizontalPosition ?? 'left'

    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        if (!show) {
            return
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === KeyboardKeyEnum.Escape) {
                event.stopPropagation()
                onCloseRef.current?.()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [show])

    return (
        <DropdownContext.Provider
            value={{
                show,
                onClose,
                horizontalPosition: effectiveHorizontal,
                verticalPosition,
            }}
        >
            <div
                {...restProps}
                className={`relative ${className}`.trim()}
            >
                {show ? <ScreenOverlay onClick={onClose} /> : null}

                <div className="relative">{children}</div>
            </div>
        </DropdownContext.Provider>
    )
}

export interface DropdownTriggerProps extends ComponentPropsWithRef<'div'> {
    children?: ReactNode
}

export function DropdownTrigger({
    children,
    className = '',
    onClick,
    onKeyDown,
    role,
    tabIndex,
    ...restProps
}: DropdownTriggerProps) {
    const { show } = useContext(DropdownContext)
    const isInteractive = Boolean(onClick)

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e)

        if (
            isInteractive &&
            (e.key === KeyboardKeyEnum.Enter || e.key === KeyboardKeyEnum.Space)
        ) {
            e.preventDefault()
            onClick?.(e as any)
        }
    }

    return (
        <div
            role={role ?? (isInteractive ? 'button' : undefined)}
            tabIndex={tabIndex ?? (isInteractive ? 0 : undefined)}
            aria-haspopup={isInteractive ? 'true' : undefined}
            aria-expanded={isInteractive ? show : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            {...restProps}
            className={`cursor-pointer flex items-center gap-4 justify-end focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-lg ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export interface DropdownMenuProps extends ComponentPropsWithRef<'div'> {
    show?: boolean
    horizontalPosition?: 'left' | 'right'
    verticalPosition?: 'top' | 'bottom'
    children?: ReactNode
}

export function DropdownMenu({
    show: propShow,
    horizontalPosition,
    verticalPosition: propVerticalPosition,
    children,
    className = '',
    onKeyDown,
    ...restProps
}: DropdownMenuProps) {
    const context = useContext(DropdownContext)
    const effectiveShow = propShow !== undefined ? propShow : context.show
    const previousActiveElementRef = useRef<HTMLElement | null>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (effectiveShow && typeof document !== 'undefined') {
            previousActiveElementRef.current =
                document.activeElement as HTMLElement | null

            const firstItem = menuRef.current?.querySelector<HTMLElement>(
                '[role="menuitem"], [role="button"], a, button',
            )

            if (firstItem) {
                firstItem.focus()
            } else {
                menuRef.current?.focus()
            }
        }
    }, [effectiveShow])

    if (!effectiveShow) {
        return null
    }

    const effectiveHorizontal =
        horizontalPosition ?? context.horizontalPosition ?? 'left'

    const effectiveVertical =
        propVerticalPosition ?? context.verticalPosition ?? 'bottom'

    const horizontalClass =
        effectiveHorizontal === 'right' ? 'left-0' : 'right-0'
    const verticalClass =
        effectiveVertical === 'top' ? 'bottom-full' : 'top-full'

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e)
        if (e.key === KeyboardKeyEnum.Escape) {
            e.preventDefault()
            context.onClose?.()

            if (
                previousActiveElementRef.current &&
                document.contains(previousActiveElementRef.current)
            ) {
                previousActiveElementRef.current.focus()
            }

            return
        }

        if (e.key === KeyboardKeyEnum.Tab) {
            const items = Array.from(
                e.currentTarget.querySelectorAll<HTMLElement>(
                    '[role="menuitem"], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ),
            )

            if (items.length > 0) {
                e.preventDefault()

                const activeEl = document.activeElement as HTMLElement | null

                const currentIndex = items.findIndex(
                    (item) => item === activeEl || item.contains(activeEl),
                )

                let nextIndex = 0

                if (e.shiftKey) {
                    nextIndex =
                        currentIndex <= 0 ? items.length - 1 : currentIndex - 1
                } else {
                    nextIndex =
                        currentIndex === -1 || currentIndex >= items.length - 1
                            ? 0
                            : currentIndex + 1
                }

                items[nextIndex].focus()
            }
            return
        }

        if (
            e.key !== KeyboardKeyEnum.ArrowDown &&
            e.key !== KeyboardKeyEnum.ArrowUp &&
            e.key !== KeyboardKeyEnum.Home &&
            e.key !== KeyboardKeyEnum.End
        ) {
            return
        }

        const items = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
        )

        if (items.length === 0) {
            return
        }

        const activeEl = document.activeElement as HTMLElement | null
        const currentIndex = items.findIndex((item) => item === activeEl)
        let nextIndex = 0

        if (e.key === KeyboardKeyEnum.ArrowDown) {
            nextIndex =
                currentIndex === -1 ? 0 : (currentIndex + 1) % items.length
        } else if (e.key === KeyboardKeyEnum.ArrowUp) {
            nextIndex =
                currentIndex === -1
                    ? items.length - 1
                    : (currentIndex - 1 + items.length) % items.length
        } else if (e.key === KeyboardKeyEnum.Home) {
            nextIndex = 0
        } else if (e.key === KeyboardKeyEnum.End) {
            nextIndex = items.length - 1
        }

        e.preventDefault()
        items[nextIndex].focus()
    }

    return (
        <div
            ref={menuRef}
            role="menu"
            aria-orientation="vertical"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            {...restProps}
            className={`absolute w-48 z-50 focus:outline-none ${horizontalClass} ${verticalClass} ${className}`.trim()}
        >
            <div className="bg-white mt-1 p-2 rounded-xl shadow-xs">
                {children}
            </div>
        </div>
    )
}

export const Dropdown = Object.assign(DropdownContainer, {
    Container: DropdownContainer,
    Trigger: DropdownTrigger,
    Menu: DropdownMenu,
    Item: DropdownItem,
})
